/**
 * Content Rendering Utilities
 * Centralized functions for rendering content items with various formatting options
 */

import React from 'react';
import { ContentItem } from '../types/slideTypes';

/**
 * Renders the text content of a content item with proper formatting
 * Handles bold text, inline bold, italic, and afterBold patterns
 */
export const renderContentText = (item: ContentItem): React.ReactNode => {
  // Handle boldText with afterBold pattern
  if (item.boldText) {
    return (
      <>
        <strong
          className={
            item.clickable
              ? 'text-blue-700 underline decoration-dotted cursor-pointer hover:text-blue-900'
              : ''
          }
        >
          {item.text}
        </strong>
        {item.afterBold}
        {item.italic && <em>{item.italic}</em>}
        {item.boldInline && <strong>{item.boldInline}</strong>}
        {item.afterInlineBold}
        {item.boldInline2 && <strong>{item.boldInline2}</strong>}
        {item.afterInlineBold2}
        {item.boldInline3 && <strong>{item.boldInline3}</strong>}
        {item.afterInlineBold3}
      </>
    );
  }

  // Handle isBold (entire text is bold)
  if (item.isBold) {
    return <strong>{item.text}</strong>;
  }

  // Handle boldInline === true (text followed by bold afterBold)
  if (item.boldInline === true) {
    return (
      <>
        {item.text}
        <strong>{item.afterBold}</strong>
      </>
    );
  }

  // Handle boldInline as string (mid-sentence bold)
  if (item.boldInline && typeof item.boldInline === 'string') {
    return (
      <>
        {item.text}
        <strong>{item.boldInline}</strong>
        {item.afterInlineBold}
        {item.boldInline2 && <strong>{item.boldInline2}</strong>}
        {item.afterInlineBold2}
        {item.boldInline3 && <strong>{item.boldInline3}</strong>}
        {item.afterInlineBold3}
      </>
    );
  }

  // Plain text
  return item.text;
};

/**
 * Renders sub-items as an indented list
 */
export const renderSubItems = (subItems: string[]): React.ReactNode => {
  return (
    <ul className="mt-2 ml-4 space-y-1 list-none">
      {subItems.map((subItem: string, subIndex: number) => (
        <li key={subIndex} className="text-lg text-slate-700 font-medium">
          {subItem}
        </li>
      ))}
    </ul>
  );
};
